import { Box } from "@mui/material";
import { COLORS, SHADOWS, RADII, FONTS } from "app/views/dashboard/shared/tokens";

// Shared restyle kit for the Employee Info detail/profile tabs.
// Mirrors the token set + conventions already established in
// apps/frontend/src/app/views/employees/{list.js,add.js}.
export const T = {
  bg: COLORS.background,
  surface: COLORS.surface,
  cardBorder: COLORS.cardBorder,
  hairline: COLORS.hairline,
  inputBorder: "#e4dff0",
  focusBorder: "#d67ce8",
  focusRing: "0 0 0 3px rgba(224,89,240,0.13)",
  gradient: COLORS.gradient,
  magenta: "#a411c1",
  ink: COLORS.ink,
  ink3: "#17131f",
  body: COLORS.body,
  body2: COLORS.body2,
  muted: COLORS.muted,
  muted2: COLORS.muted2,
  danger: COLORS.danger,
  dangerText: COLORS.dangerText,
  dangerBg: COLORS.dangerBg,
  success: COLORS.success,
  successText: COLORS.successText,
  successBg: COLORS.successBg,
  warning: COLORS.warning,
  warningText: COLORS.warningText,
  warningBg: COLORS.warningBg,
  shadow: SHADOWS.card,
  gradientBtnShadow: SHADOWS.gradientButton,
  cardR: RADII.card,
  inputR: "10px",
  heading: FONTS.heading,
  body_f: FONTS.body,
};

// ── Section card (mirrors add.js sectionCardSx) ────────────────
export const sectionCardSx = {
  background: T.surface,
  border: `1px solid ${T.cardBorder}`,
  borderRadius: T.cardR,
  padding: "20px 22px 22px",
  boxShadow: T.shadow,
  marginBottom: "16px",
};

// ── MUI TextField / Select sx override — applied via the `sx` prop  ──
// so existing name/value/onChange/onBlur/error/helperText wiring on
// each field is untouched; only the visual chrome changes.
export const muiFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: T.inputR,
    fontFamily: T.body_f,
    fontSize: "14.5px",
    background: T.surface,
    "& fieldset": { borderColor: T.inputBorder },
    "&:hover fieldset": { borderColor: T.focusBorder },
    "&.Mui-focused fieldset": { borderColor: T.focusBorder, boxShadow: T.focusRing },
    "&.Mui-error fieldset": { borderColor: "#e5899a" },
  },
  "& .MuiInputLabel-root": { fontFamily: T.body_f, fontSize: "14px" },
  "& .MuiFormHelperText-root.Mui-error": { color: T.danger, fontSize: "12px", marginLeft: 0 },
};

export function SectionHeader({ title, sub, action }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "14px", mb: "18px", flexWrap: "wrap" }}>
      <Box>
        <Box sx={{ fontFamily: T.heading, fontSize: "16px", fontWeight: 600, color: T.ink }}>{title}</Box>
        {sub && <Box sx={{ fontSize: "12.5px", color: T.muted, mt: "3px" }}>{sub}</Box>}
      </Box>
      {action}
    </Box>
  );
}

// ── Status chip (Approved / Pending / Existing / New / etc.) ──────
const CHIP_TONES = {
  success: { bg: T.successBg, color: T.successText },
  warning: { bg: T.warningBg, color: T.warningText },
  danger: { bg: T.dangerBg, color: T.dangerText },
  neutral: { bg: T.hairline, color: T.body2 },
  brand: { bg: "rgba(224,89,240,0.1)", color: T.magenta },
};
export function StatusChip({ label, tone = "neutral" }) {
  const c = CHIP_TONES[tone] || CHIP_TONES.neutral;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        fontFamily: T.body_f,
        fontSize: "11.5px",
        fontWeight: 600,
        color: c.color,
        background: c.bg,
        borderRadius: "6px",
        padding: "3px 9px",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
}

// ── Buttons (gradient primary / outline secondary / ghost danger) ──
export function PrimaryButton({ children, disabled, sx, ...rest }) {
  return (
    <Box
      component="button"
      type="submit"
      disabled={disabled}
      sx={{
        border: "none",
        borderRadius: T.inputR,
        padding: "10px 20px",
        fontFamily: T.body_f,
        fontSize: "14px",
        fontWeight: 600,
        cursor: disabled ? "default" : "pointer",
        pointerEvents: disabled ? "none" : "auto",
        background: disabled ? "#e6e0f0" : T.gradient,
        color: disabled ? T.muted2 : "#fff",
        boxShadow: disabled ? "none" : T.gradientBtnShadow,
        transition: "background 0.18s, box-shadow 0.18s",
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
}

export function SecondaryButton({ children, disabled, sx, ...rest }) {
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      sx={{
        border: `1px solid ${T.cardBorder}`,
        borderRadius: T.inputR,
        padding: "10px 18px",
        fontFamily: T.body_f,
        fontSize: "14px",
        fontWeight: 500,
        cursor: disabled ? "default" : "pointer",
        pointerEvents: disabled ? "none" : "auto",
        background: T.surface,
        color: disabled ? T.muted2 : T.body,
        "&:hover": disabled ? {} : { background: T.hairline },
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
}

// ── Loading skeleton (replaces the bare CircularProgress + text) ──
export function LoadingBlock({ label = "Loading…" }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", py: "10px" }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            height: 54,
            borderRadius: T.inputR,
            background: `linear-gradient(90deg, ${T.hairline} 25%, #f0ecf7 37%, ${T.hairline} 63%)`,
            backgroundSize: "400% 100%",
            animation: "tt-shimmer 1.4s ease infinite",
            "@keyframes tt-shimmer": {
              "0%": { backgroundPosition: "100% 50%" },
              "100%": { backgroundPosition: "0% 50%" },
            },
          }}
        />
      ))}
      <Box sx={{ fontSize: "12.5px", color: T.muted, textAlign: "center" }}>{label}</Box>
    </Box>
  );
}

// ── Inline error banner (surfaces fetch failures visually) ────────
export function ErrorBlock({ message = "Something went wrong.", onRetry }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        padding: "13px 16px",
        borderRadius: T.inputR,
        background: T.dangerBg,
        border: "1px solid rgba(224,84,104,0.2)",
        fontFamily: T.body_f,
        fontSize: "13px",
        color: T.dangerText,
        marginBottom: "14px",
        flexWrap: "wrap",
      }}
    >
      <span>{message}</span>
      {onRetry && (
        <Box
          component="button"
          type="button"
          onClick={onRetry}
          sx={{
            border: "none",
            background: "none",
            color: T.dangerText,
            fontWeight: 600,
            fontSize: "13px",
            cursor: "pointer",
            textDecoration: "underline",
            fontFamily: T.body_f,
          }}
        >
          Retry
        </Box>
      )}
    </Box>
  );
}
